import React, { useState, useEffect, useCallback } from "react";
import { API } from "../hooks/useApi";

const card = {
  background: "#0f1629",
  border: "1px solid #1e2d4a",
  borderRadius: "8px",
  padding: "16px",
  marginBottom: "16px",
};

const label = { fontSize: "11px", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "4px" };
const value = { fontSize: "14px", color: "#e2e8f0", fontWeight: 500 };
const input = {
  background: "#0a0f1e", border: "1px solid #1e2d4a", borderRadius: "6px",
  color: "#e2e8f0", padding: "8px 10px", fontSize: "13px", width: "100%", boxSizing: "border-box",
};
const btn = (color = "#3b82f6") => ({
  background: color, border: "none", borderRadius: "6px", color: "#fff",
  padding: "8px 16px", fontSize: "13px", cursor: "pointer", fontWeight: 500,
});
const row = { display: "flex", gap: "12px", marginBottom: "12px", flexWrap: "wrap" };
const col = { flex: 1, minWidth: "140px" };

const SECTIONS = [
  { id: "fund",    label: "💰 Fund Profile" },
  { id: "risk",    label: "⚖️ Risk Limits" },
  { id: "assets",  label: "📡 Assets" },
  { id: "reset",   label: "🔄 Account Reset" },
  { id: "system",  label: "⚙️ System" },
];

export default function Settings() {
  const [section, setSection]   = useState("fund");
  const [settings, setSettings] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState(null);
  const [form, setForm]         = useState({});

  // Load current settings
  useEffect(() => {
    API.get("/settings").then(r => {
      setSettings(r.data);
      setForm(r.data);
    }).catch(() => {
      // Fallback defaults if endpoint not yet deployed
      const defaults = {
        start_balance: 5000.0, monthly_injection: 200.0, base_date: "2026-04-14",
        base_risk_pct: 1.0, surplus_risk_pct: 2.0,
        kill_switch_pct: 15.0, vix_limit: 30.0, roro_threshold: 18.5,
        max_concurrent_trades: 2,
      };
      setSettings(defaults);
      setForm(defaults);
    });
  }, []);

  const save = async (payload) => {
    setSaving(true); setMsg(null);
    try {
      await API.post("/settings", payload);
      setMsg({ type: "ok", text: "Settings saved. Brain will apply on next cycle." });
      setSettings({ ...settings, ...payload });
    } catch (e) {
      setMsg({ type: "err", text: e?.response?.data?.detail || "Save failed" });
    }
    setSaving(false);
  };

  const resetDB = async () => {
    if (!window.confirm("This will DELETE all trade history, equity snapshots, and brain events. Continue?")) return;
    setSaving(true); setMsg(null);
    try {
      await API.post("/settings/reset_db");
      setMsg({ type: "ok", text: "Database reset complete. Fresh start!" });
    } catch (e) {
      setMsg({ type: "err", text: e?.response?.data?.detail || "Reset failed" });
    }
    setSaving(false);
  };

  // v5.25a (2026-09-17): per-asset trading switch. The roster comes from the API, never a
  // literal. The backend requires admin for both calls; this tab is mounted for admins only.
  const [assets, setAssets]       = useState(null);
  const [assetsErr, setAssetsErr] = useState(null);
  const [assetBusy, setAssetBusy] = useState(null);

  const loadAssets = useCallback(() => {
    API.get("/settings/assets")
      .then(r => { setAssets(r.data?.assets || []); setAssetsErr(null); })
      .catch(e => setAssetsErr(e?.response?.data?.detail || "Could not load asset switches"));
  }, []);

  useEffect(() => { if (section === "assets") loadAssets(); }, [section, loadAssets]);

  const toggleAsset = async (a) => {
    const enable = !a.trading_enabled;
    const question = enable
      ? `Orders will be placed for ${a.symbol} on this account. Enable?`
      : `${a.symbol} will stop opening new trades. Open positions are NOT closed and are still managed. Disable?`;
    if (!window.confirm(question)) return;
    setAssetBusy(a.symbol); setMsg(null);
    try {
      const r = await API.post("/settings/assets", { symbol: a.symbol, enabled: enable });
      const verb = enable ? "enabled" : "disabled";
      const same = r.data?.changed === false ? " (it already was)" : "";
      setMsg({ type: "ok", text: `${a.symbol} trading ${verb}${same}. The Asset grid can take ~45 s to update.` });
    } catch (e) {
      setMsg({ type: "err", text: e?.response?.data?.detail || "Save failed" });
    }
    setAssetBusy(null);
    loadAssets();
  };

  const f = (key) => form[key] ?? "";
  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  if (!settings) return <div style={{ padding: 20, color: "#64748b" }}>Loading settings…</div>;

  return (
    <div style={{ padding: "16px", maxWidth: 900 }}>
      <h2 style={{ color: "#e2e8f0", fontSize: 18, marginBottom: 16 }}>⚙️ Sentinel Settings</h2>

      {/* Section tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        {SECTIONS.map(s => (
          <button key={s.id} onClick={() => setSection(s.id)} style={{
            ...btn(section === s.id ? "#1e40af" : "#1e2d4a"),
            border: section === s.id ? "1px solid #3b82f6" : "1px solid #1e2d4a",
          }}>{s.label}</button>
        ))}
      </div>

      {/* Message */}
      {msg && (
        <div style={{ padding: "10px 14px", borderRadius: 6, marginBottom: 16, fontSize: 13,
          background: msg.type === "ok" ? "#052e16" : "#450a0a",
          border: `1px solid ${msg.type === "ok" ? "#16a34a" : "#dc2626"}`,
          color: msg.type === "ok" ? "#4ade80" : "#f87171" }}>
          {msg.text}
        </div>
      )}

      {/* ── FUND PROFILE ── */}
      {section === "fund" && (
        <div style={card}>
          <h3 style={{ color: "#93c5fd", fontSize: 14, marginBottom: 16 }}>💰 Fund Profile</h3>
          <div style={row}>
            <div style={col}>
              <div style={label}>Start Balance ($)</div>
              <input style={input} type="number" value={f("start_balance")}
                onChange={e => set("start_balance", parseFloat(e.target.value))} />
              <div style={{ fontSize: 11, color: "#475569", marginTop: 4 }}>
                Initial account balance (e.g. 5000 for live, 100000 for demo)
              </div>
            </div>
            <div style={col}>
              <div style={label}>Monthly Injection ($)</div>
              <input style={input} type="number" value={f("monthly_injection")}
                onChange={e => set("monthly_injection", parseFloat(e.target.value))} />
              <div style={{ fontSize: 11, color: "#475569", marginTop: 4 }}>
                Monthly capital added to account
              </div>
            </div>
            <div style={col}>
              <div style={label}>Base Date</div>
              <input style={input} type="date" value={f("base_date")}
                onChange={e => set("base_date", e.target.value)} />
              <div style={{ fontSize: 11, color: "#475569", marginTop: 4 }}>
                Start date for injection calculation
              </div>
            </div>
          </div>

          {/* Live preview */}
          <div style={{ background: "#0a0f1e", borderRadius: 6, padding: "12px 14px", marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: "#64748b", marginBottom: 8 }}>Preview — Risk Calculation</div>
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              {[
                ["Base Risk (1%)", `$${((f("start_balance") || 0) * (f("base_risk_pct") || 1) / 100).toFixed(2)}`],
                ["Kill Switch at", `$${((f("start_balance") || 0) * (1 - (f("kill_switch_pct") || 15) / 100)).toFixed(2)}`],
                ["Max Portfolio Risk (4%)", `$${((f("start_balance") || 0) * 0.04).toFixed(2)}`],
              ].map(([k, v]) => (
                <div key={k}>
                  <div style={{ fontSize: 11, color: "#64748b" }}>{k}</div>
                  <div style={{ fontSize: 14, color: "#60a5fa", fontWeight: 600 }}>{v}</div>
                </div>
              ))}
            </div>
          </div>

          <button style={btn()} disabled={saving}
            onClick={() => save({ start_balance: f("start_balance"), monthly_injection: f("monthly_injection"), base_date: f("base_date") })}>
            {saving ? "Saving…" : "💾 Save Fund Profile"}
          </button>
        </div>
      )}

      {/* ── RISK LIMITS ── */}
      {section === "risk" && (
        <div style={card}>
          <h3 style={{ color: "#93c5fd", fontSize: 14, marginBottom: 16 }}>⚖️ Risk Limits</h3>
          <div style={row}>
            <div style={col}>
              <div style={label}>Base Risk % (on principal)</div>
              <input style={input} type="number" step="0.1" value={f("base_risk_pct")}
                onChange={e => set("base_risk_pct", parseFloat(e.target.value))} />
              <div style={{ fontSize: 11, color: "#475569", marginTop: 4 }}>Default: 1%</div>
            </div>
            <div style={col}>
              <div style={label}>Surplus Risk % (on profits)</div>
              <input style={input} type="number" step="0.5" value={f("surplus_risk_pct")}
                onChange={e => set("surplus_risk_pct", parseFloat(e.target.value))} />
              <div style={{ fontSize: 11, color: "#475569", marginTop: 4 }}>Default: 2% (true 1-2-4 model)</div>
            </div>
          </div>
          <div style={row}>
            <div style={col}>
              <div style={label}>Kill Switch Drawdown %</div>
              <input style={input} type="number" step="1" value={f("kill_switch_pct")}
                onChange={e => set("kill_switch_pct", parseFloat(e.target.value))} />
              <div style={{ fontSize: 11, color: "#475569", marginTop: 4 }}>
                Close all + block entries if drawdown exceeds this. Default: 15%
              </div>
            </div>
            <div style={col}>
              <div style={label}>VIX Upper Limit</div>
              <input style={input} type="number" step="1" value={f("vix_limit")}
                onChange={e => set("vix_limit", parseFloat(e.target.value))} />
              <div style={{ fontSize: 11, color: "#475569", marginTop: 4 }}>
                Suspend entries above this VIX. Default: 30
              </div>
            </div>
            <div style={col}>
              <div style={label}>RORO Risk-Off Threshold</div>
              <input style={input} type="number" step="0.5" value={f("roro_threshold")}
                onChange={e => set("roro_threshold", parseFloat(e.target.value))} />
              <div style={{ fontSize: 11, color: "#475569", marginTop: 4 }}>
                Below = risk-off, halve sizes. Default: 18.5
              </div>
            </div>
          </div>
          <button style={btn()} disabled={saving}
            onClick={() => save({ base_risk_pct: f("base_risk_pct"), surplus_risk_pct: f("surplus_risk_pct"), kill_switch_pct: f("kill_switch_pct"), vix_limit: f("vix_limit"), roro_threshold: f("roro_threshold") })}>
            {saving ? "Saving…" : "💾 Save Risk Limits"}
          </button>
        </div>
      )}

      {/* ── ASSETS (v5.25a) ── */}
      {section === "assets" && (
        <div style={card}>
          <h3 style={{ color: "#93c5fd", fontSize: 14, marginBottom: 8 }}>📡 Asset Trading</h3>
          <p style={{ fontSize: 12, color: "#94a3b8", marginBottom: 16 }}>
            Unchecked assets place no new orders but keep learning: models retrain, signals are
            generated, and a signal that passes every gate is recorded as would-have-traded.
            Open positions are not closed and are still managed.
          </p>
          {assetsErr && <div style={{ fontSize: 12, color: "#f87171", marginBottom: 12 }}>{assetsErr}</div>}
          {!assets && !assetsErr && <div style={{ fontSize: 12, color: "#64748b" }}>Loading assets…</div>}
          {assets && assets.map(a => (
            <label key={a.symbol} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0",
              borderBottom: "1px solid #1e2d4a", cursor: a.source === "error" ? "not-allowed" : "pointer" }}>
              <input type="checkbox" checked={!!a.trading_enabled}
                disabled={assetBusy !== null || a.source === "error"}
                onChange={() => toggleAsset(a)} />
              <span style={{ ...value, width: 80 }}>{a.symbol}</span>
              <span style={{ fontSize: 12, color: a.trading_enabled ? "#4ade80" : "#f59e0b", width: 150 }}>
                {a.trading_enabled ? "trading on" : "trading off — learning only"}
              </span>
              <span style={{ fontSize: 11, color: a.source === "error" ? "#f87171" : "#64748b", width: 150 }}>
                {a.source === "db" ? "set in Settings"
                  : a.source === "default" ? `default (${a.default_enabled ? "on" : "off"})`
                  : "database unreadable"}
              </span>
              <span style={{ fontSize: 11, color: "#64748b" }}>
                {a.updated_at ? `last change ${new Date(a.updated_at).toLocaleString()}${a.updated_by ? ` by ${a.updated_by}` : ""}` : "never changed"}
              </span>
            </label>
          ))}
        </div>
      )}

      {/* ── ACCOUNT RESET ── */}
      {section === "reset" && (
        <div style={card}>
          <h3 style={{ color: "#f87171", fontSize: 14, marginBottom: 16 }}>🔄 Account Reset</h3>
          <p style={{ fontSize: 13, color: "#94a3b8", marginBottom: 16 }}>
            Use this when switching to a new demo/live account. This clears all trade history,
            equity snapshots, and brain events so the system starts fresh with the new account.
          </p>

          <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
            <div style={{ flex: 1, background: "#1a0a0a", border: "1px solid #7f1d1d", borderRadius: 6, padding: "12px 14px" }}>
              <div style={{ fontSize: 12, color: "#f87171", marginBottom: 8, fontWeight: 600 }}>⚠️ Deleted (trading activity):</div>
              <ul style={{ fontSize: 12, color: "#fca5a5", margin: 0, paddingLeft: 16 }}>
                <li>trade_logs (history, open positions)</li>
                <li>brain_events (AI decision log)</li>
                <li>equity_snapshots (equity curve)</li>
                <li>position_snapshots</li>
                <li>injections</li>
              </ul>
            </div>
            <div style={{ flex: 1, background: "#052e16", border: "1px solid #166534", borderRadius: 6, padding: "12px 14px" }}>
              <div style={{ fontSize: 12, color: "#4ade80", marginBottom: 8, fontWeight: 600 }}>✅ Preserved (AI training data):</div>
              <ul style={{ fontSize: 12, color: "#86efac", margin: 0, paddingLeft: 16 }}>
                <li>historical_ohlcv (107k+ H1 bars)</li>
                <li>macro_history (VIX/DXY/yield/RORO)</li>
                <li>training_data (WIN/LOSS outcomes)</li>
                <li>LightGBM models (in memory)</li>
              </ul>
            </div>
          </div>

          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ flex: 1 }}>
              <div style={label}>New Start Balance ($)</div>
              <input style={input} type="number" value={f("start_balance")}
                onChange={e => set("start_balance", parseFloat(e.target.value))}
                placeholder="e.g. 5000" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={label}>New Base Date</div>
              <input style={input} type="date" value={f("base_date")}
                onChange={e => set("base_date", e.target.value)} />
            </div>
          </div>

          <div style={{ marginTop: 16, display: "flex", gap: 12 }}>
            <button style={btn("#dc2626")} disabled={saving} onClick={resetDB}>
              {saving ? "Resetting…" : "🗑️ Reset Database (Fresh Start)"}
            </button>
            <button style={btn()} disabled={saving}
              onClick={() => save({ start_balance: f("start_balance"), base_date: f("base_date") })}>
              {saving ? "Saving…" : "💾 Update Balance Only"}
            </button>
          </div>
        </div>
      )}

      {/* ── SYSTEM ── */}
      {section === "system" && (
        <div style={card}>
          <h3 style={{ color: "#93c5fd", fontSize: 14, marginBottom: 16 }}>⚙️ System Settings</h3>
          <div style={row}>
            <div style={col}>
              <div style={label}>Max Concurrent Trades</div>
              <input style={input} type="number" min="1" max="6" value={f("max_concurrent_trades")}
                onChange={e => set("max_concurrent_trades", parseInt(e.target.value))} />
            </div>
          </div>

          {/* Current config display */}
          <div style={{ background: "#0a0f1e", borderRadius: 6, padding: "12px 14px", marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: "#64748b", marginBottom: 10 }}>Current Active Configuration</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12 }}>
              {settings && Object.entries({
                "Start Balance":    `$${settings.start_balance?.toLocaleString()}`,
                "Monthly Injection":`$${settings.monthly_injection}`,
                "Base Date":         settings.base_date,
                "Base Risk":        `${settings.base_risk_pct}%`,
                "Surplus Risk":     `${settings.surplus_risk_pct}%`,
                "Kill Switch":      `${settings.kill_switch_pct}%`,
                "VIX Limit":         settings.vix_limit,
                "RORO Threshold":    settings.roro_threshold,
              }).map(([k, v]) => (
                <div key={k}>
                  <div style={label}>{k}</div>
                  <div style={value}>{v}</div>
                </div>
              ))}
            </div>
          </div>

          <button style={btn()} disabled={saving}
            onClick={() => save({ max_concurrent_trades: f("max_concurrent_trades") })}>
            {saving ? "Saving…" : "💾 Save System Settings"}
          </button>
        </div>
      )}
    </div>
  );
}
